import { useState, useCallback, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Spinner } from 'reactstrap';
import { useUser } from '@clerk/clerk-react';
import { BiCheck, BiImage, BiPalette, BiFile, BiMapPin } from 'react-icons/bi';
import { GridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import useIsOrgAdmin from '../hooks/useIsOrgAdmin';
import useOrgRole from '../hooks/useOrgRole';
import useCompanyBranding from '../hooks/useCompanyBranding';
import useDashboardSummary from '../hooks/useDashboardSummary';
import KpiCard from '../components/dashboard/KpiCard';
import ProfitChart from '../components/dashboard/ProfitChart';
import RevenueCostChart from '../components/dashboard/RevenueCostChart';
import ExpenseBreakdownChart from '../components/dashboard/ExpenseBreakdownChart';
import AlertsList from '../components/dashboard/AlertsList';
import InsightsSection from '../components/dashboard/InsightsSection';
import WorkerDashboard from '../components/dashboard/WorkerDashboard';

// ── Content-width measurement ──────────────────────────────────────────────
// Measures the actual available content width (viewport minus sidebar) rather
// than window.innerWidth. Seeded synchronously so the first render is correct.
function useContentWidth() {
    const ref = useRef(null);
    const [width, setWidth] = useState(() => window.innerWidth);

    useLayoutEffect(() => {
        if (!ref.current) return;
        setWidth(ref.current.offsetWidth);
        const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return { ref, width };
}

// ── Desktop RGL wrapper ────────────────────────────────────────────────────
// useLayoutEffect measures the container synchronously before the first paint
// so GridLayout renders at the correct width with no flash.
function RGL({ children, ...props }) {
    const containerRef = useRef(null);
    const [width, setWidth] = useState(0);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        setWidth(containerRef.current.offsetWidth);
        const observer = new ResizeObserver(entries => {
            setWidth(entries[0].contentRect.width);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef}>
            {width > 0 && (
                <GridLayout width={width} {...props}>
                    {children}
                </GridLayout>
            )}
        </div>
    );
}

// ── Desktop grid config ────────────────────────────────────────────────────
const COLS       = 12;
const ROW_HEIGHT = 40;
const LS_KEY     = 'marginpal_dashboard_layout_v3';

const DEFAULT_LAYOUT = [
    { i: 'kpi-0',        x: 0,  y: 0,  w: 2,  h: 1, minW: 2, minH: 1 },
    { i: 'kpi-1',        x: 2,  y: 0,  w: 2,  h: 1, minW: 2, minH: 1 },
    { i: 'kpi-2',        x: 4,  y: 0,  w: 2,  h: 1, minW: 2, minH: 1 },
    { i: 'kpi-3',        x: 6,  y: 0,  w: 2,  h: 1, minW: 2, minH: 1 },
    { i: 'kpi-4',        x: 8,  y: 2,  w: 2,  h: 1, minW: 2, minH: 1 },
    { i: 'kpi-5',        x: 10, y: 2,  w: 2,  h: 1, minW: 2, minH: 1 },
    { i: 'profit-chart', x: 0,  y: 1,  w: 8,  h: 3, minW: 4, minH: 3 },
    { i: 'alerts',       x: 8,  y: 0,  w: 4,  h: 3, minW: 6, minH: 3 },
    { i: 'expense-pie',  x: 0,  y: 2,  w: 4,  h: 3, minW: 3, minH: 3 },
    { i: 'rev-cost',     x: 4,  y: 2,  w: 8,  h: 3, minW: 6, minH: 4 },
];

const DEFAULT_KEYS = DEFAULT_LAYOUT.map(l => l.i).sort().join(',');

function loadLayout() {
    try {
        const saved = JSON.parse(localStorage.getItem(LS_KEY) ?? 'null');
        if (saved) {
            const savedKeys = saved.map(l => l.i).sort().join(',');
            if (savedKeys === DEFAULT_KEYS) return saved;
        }
    } catch { /* corrupted — fall back */ }
    return DEFAULT_LAYOUT;
}

// ── Branding setup reminder ────────────────────────────────────────────────
function BrandingSetupReminder() {
    const isAdmin = useIsOrgAdmin();
    const { data: branding, isLoading } = useCompanyBranding();

    if (!isAdmin || isLoading || !branding) return null;

    const hasContactDetails = !!(branding.addressLine1 || branding.phone || branding.email);

    const items = [
        { key: 'logo',     done: !!branding.logoUrl,       Icon: BiImage,   label: 'Upload your company logo'    },
        { key: 'colour',   done: !!branding.brandColour,   Icon: BiPalette, label: 'Set your brand colour'       },
        { key: 'template', done: !!branding.quoteTemplate, Icon: BiFile,    label: 'Choose a quote template'     },
        { key: 'contact',  done: hasContactDetails,        Icon: BiMapPin,  label: 'Add your contact details'    },
    ];

    if (items.every(i => i.done)) return null;

    return (
        <div className="surface p-4 mb-4">
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                    <h2 className="fw-semibold fs-5 mb-1">Set up your quote branding</h2>
                    <p className="text-muted small mb-3">
                        Personalise your quote PDFs so they look professional and represent your business.
                    </p>
                    <div className="d-flex flex-column gap-2">
                        {items.map(({ key, done, Icon, label }) => (
                            <div key={key} className="d-flex align-items-center gap-2 small">
                                {done
                                    ? <BiCheck size={18} className="text-success flex-shrink-0" />
                                    : <Icon   size={18} className="text-muted flex-shrink-0" />}
                                <span className={done ? 'text-muted text-decoration-line-through' : ''}>{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <Link to="/settings">
                    <button className="btn btn-primary btn-sm text-nowrap">Go to Settings</button>
                </Link>
            </div>
        </div>
    );
}

// ── Mobile layout — simple stacked, no drag/resize ────────────────────────
function MobileDashboard({ kpis, monthlyTimeSeries, expenseBreakdown, alerts, marginDisplay, baseCurrency }) {
    return (
        <div className="d-flex flex-column gap-3">
            <div className="row g-2">
                <div className="col-6" style={{ height: 90 }}>
                    <KpiCard label="Revenue this month" value={kpis.revenueThisMonth ?? 0} isCurrency currency={baseCurrency} />
                </div>
                <div className="col-6" style={{ height: 90 }}>
                    <KpiCard label="Costs this month" value={kpis.costsThisMonth ?? 0} isCurrency currency={baseCurrency} />
                </div>
                <div className="col-6" style={{ height: 90 }}>
                    <KpiCard
                        label="Profit this month"
                        value={kpis.profitThisMonth ?? 0}
                        isCurrency
                        currency={baseCurrency}
                        tone={(kpis.profitThisMonth ?? 0) < 0 ? 'danger' : null}
                    />
                </div>
                <div className="col-6" style={{ height: 90 }}>
                    <KpiCard
                        label="Margin %"
                        displayValue={marginDisplay}
                        tone={kpis.marginPercent != null && kpis.marginPercent < 20 ? 'warning' : null}
                    />
                </div>
                <div className="col-6" style={{ height: 90 }}>
                    <KpiCard label="Active Jobs" value={kpis.activeJobs ?? 0} />
                </div>
                <div className="col-6" style={{ height: 90 }}>
                    <KpiCard
                        label="Jobs Over Budget"
                        value={kpis.jobsOverBudget ?? 0}
                        tone={(kpis.jobsOverBudget ?? 0) > 0 ? 'danger' : null}
                    />
                </div>
            </div>
            <div style={{ height: 280 }}><ProfitChart data={monthlyTimeSeries} currency={baseCurrency} /></div>
            <div style={{ height: 280 }}><AlertsList alerts={alerts} /></div>
            <div style={{ height: 280 }}><ExpenseBreakdownChart data={expenseBreakdown} currency={baseCurrency} /></div>
            <div style={{ height: 280 }}><RevenueCostChart data={monthlyTimeSeries} currency={baseCurrency} /></div>
        </div>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function DashboardPage() {
    const { user } = useUser();
    const displayName = user?.firstName ?? user?.primaryEmailAddress?.emailAddress ?? 'User';
    // Workers get a different dashboard surface (recent receipts +
    // attention items) — the manager-only GetSummary endpoint 403s for
    // them and exposes margin/profit they shouldn't see anyway. All
    // hooks are called unconditionally above the early return so React's
    // rules-of-hooks are satisfied across role transitions.
    const role = useOrgRole();

    const { data, isLoading, isError } = useDashboardSummary();
    const { ref: measureRef, width: contentWidth } = useContentWidth();
    const isMobile = contentWidth < 768;
    const [layout, setLayout] = useState(loadLayout);

    const handleLayoutChange = useCallback((newLayout) => {
        setLayout(newLayout);
        try { localStorage.setItem(LS_KEY, JSON.stringify(newLayout)); } catch { /* storage full */ }
    }, []);

    const handleReset = useCallback(() => {
        setLayout(DEFAULT_LAYOUT);
        try { localStorage.removeItem(LS_KEY); } catch {}
    }, []);

    const { kpis = {}, monthlyTimeSeries = [], expenseBreakdown = [], alerts = [], baseCurrency = 'GBP' } = data ?? {};
    const marginDisplay = kpis.marginPercent == null ? '—' : `${Number(kpis.marginPercent).toFixed(1)}%`;

    // Worker branch comes after all hooks have been called (rules of
    // hooks). The data fetched above is unused on this path — its 403
    // is swallowed by useDashboardSummary.
    if (role === 'Worker') return <WorkerDashboard />;

    return (
        <div>
            {/* Zero-height div that measures the available content width (excludes sidebar).
                isMobile is derived from this, not window.innerWidth. */}
            <div ref={measureRef} style={{ width: '100%', height: 0 }} />
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start gap-3 mb-1 flex-wrap">
                <div>
                    <h1 className="fw-bold mb-0">Dashboard</h1>
                    <p className="text-muted mb-0">Welcome back, {displayName}.</p>
                </div>
                {!isLoading && !isMobile && (
                    <Button size="sm" color="secondary" outline onClick={handleReset}>
                        Reset layout
                    </Button>
                )}
            </div>
            <p className="text-muted small mb-4">
                Shown in {baseCurrency} · multi-currency jobs converted at transaction-date rates.
            </p>

            <BrandingSetupReminder />

            <InsightsSection />

            {isLoading && !data ? (
                <div className="surface d-flex flex-column align-items-center justify-content-center gap-3 py-5 my-2" style={{ minHeight: '40vh' }}>
                    <Spinner style={{ width: '2.5rem', height: '2.5rem' }} />
                    <div className="text-center">
                        <h4 className="fw-semibold mb-1">Welcome to MarginPal</h4>
                        <p className="text-muted mb-0 small">Pulling together your data — just a moment.</p>
                    </div>
                </div>
            ) : isError && !data ? (
                <div className="surface text-center text-danger py-5">
                    Failed to load dashboard data. Please refresh.
                </div>
            ) : null}

            {data && (isMobile ? (
                <MobileDashboard
                    kpis={kpis}
                    monthlyTimeSeries={monthlyTimeSeries}
                    expenseBreakdown={expenseBreakdown}
                    alerts={alerts}
                    marginDisplay={marginDisplay}
                    baseCurrency={baseCurrency}
                />
            ) : (
                <RGL
                    layout={layout}
                    cols={COLS}
                    rowHeight={ROW_HEIGHT}
                    onLayoutChange={handleLayoutChange}
                    draggableHandle=".drag-handle"
                    margin={[12, 12]}
                    containerPadding={[0, 0]}
                    isResizable
                    isDraggable
                    compactType={null}
                >
                    <div key="kpi-0" style={{ height: '100%' }}><KpiCard label="Revenue this month" value={kpis.revenueThisMonth ?? 0} isCurrency currency={baseCurrency} /></div>
                    <div key="kpi-1" style={{ height: '100%' }}><KpiCard label="Costs this month"   value={kpis.costsThisMonth   ?? 0} isCurrency currency={baseCurrency} /></div>
                    <div key="kpi-2" style={{ height: '100%' }}>
                        <KpiCard
                            label="Profit this month"
                            value={kpis.profitThisMonth ?? 0}
                            isCurrency
                            currency={baseCurrency}
                            tone={(kpis.profitThisMonth ?? 0) < 0 ? 'danger' : null}
                        />
                    </div>
                    <div key="kpi-3" style={{ height: '100%' }}>
                        <KpiCard
                            label="Margin %"
                            displayValue={marginDisplay}
                            tone={kpis.marginPercent != null && kpis.marginPercent < 20 ? 'warning' : null}
                        />
                    </div>
                    <div key="kpi-4" style={{ height: '100%' }}><KpiCard label="Active Jobs" value={kpis.activeJobs ?? 0} /></div>
                    <div key="kpi-5" style={{ height: '100%' }}>
                        <KpiCard
                            label="Jobs Over Budget"
                            value={kpis.jobsOverBudget ?? 0}
                            tone={(kpis.jobsOverBudget ?? 0) > 0 ? 'danger' : null}
                        />
                    </div>
                    <div key="profit-chart" style={{ height: '100%' }}><ProfitChart data={monthlyTimeSeries} currency={baseCurrency} /></div>
                    <div key="expense-pie"  style={{ height: '100%' }}><ExpenseBreakdownChart data={expenseBreakdown} currency={baseCurrency} /></div>
                    <div key="rev-cost"     style={{ height: '100%' }}><RevenueCostChart data={monthlyTimeSeries} currency={baseCurrency} /></div>
                    <div key="alerts"       style={{ height: '100%' }}><AlertsList alerts={alerts} /></div>
                </RGL>
            ))}
        </div>
    );
}
